import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import PageContainer from '@deliveroo/consumer-component-library/components/PageContainer';
import CuisineRestaurantsContainer from '../containers/CuisineRestaurantsContainer';
import RestaurantReviewsContainer from '../containers/RestaurantReviewsContainer';
import LocationListContainer from '../containers/CuisineLocationListContainer';
import RecommendedCuisinesContainer from '../containers/RecommendedCuisinesContainer';
import PageFooter from '../containers/PageFooter';
import CuisinePageHeroContainer from '../containers/CuisinePageHeroContainer';
import CuisinePageFooter from '../containers/CuisinePageFooterContainer';
import Page from '../containers/Page';
import HowDeliverooWorksContainer from '../containers/HowDeliverooWorksContainer';
import AnalyticsTrackers from '../containers/AnalyticsTrackers';
import withI18n from '../components/withI18n';
import { requestConfig } from '../actions/configActions';
import { requestRecentAddresses } from '../actions/locationActions';
import {
  requestCuisineForCountry,
  requestCuisineForCity,
  getRestaurantsByAddresses,
} from '../actions/cuisineActions';
import { alternateLocaleUrlsSelector, hasQueryParamsSelector } from '../selectors/cuisineSelectors';
import {
  statusForState,
  isCuisineRestaurantsOn,
  getCuisineLandingPagePath,
} from '../utils/pages/cuisines';
import sentry from '../utils/sentry';
import CustomErrorPage from './_error';

const { captureException } = sentry();

class Cuisines extends Component {
  static async getPageData(context, dispatch, getState) {
    const { res, query } = context;
    const cuisineAction = query.city ? requestCuisineForCity : requestCuisineForCountry;

    await Promise.all([
      dispatch(requestConfig()),
      dispatch(cuisineAction(query)),
      dispatch(requestRecentAddresses()),
    ]);

    const state = getState();
    const cuisineRestaurantsOn = isCuisineRestaurantsOn(state.config);

    if (cuisineRestaurantsOn) {
      const { locations } = state.location.recentAddresses;
      await dispatch(getRestaurantsByAddresses(locations));
    }

    const { status, error } = statusForState(state);

    if (res && status) {
      res.statusCode = status;

      if (error) {
        captureException(error);
      }
    }
  }

  render() {
    const {
      statusCode,
      pageTitle,
      metaDescription,
      landingPagePath,
      cuisineRestaurantsOn,
      alternateLocales,
      robots,
    } = this.props;

    switch (true) {
      case statusCode === 200: {
        return (
          <Page
            pageName="cuisine"
            color="anchovy"
            pageTitle={pageTitle}
            metaDescription={metaDescription}
            pagePath={landingPagePath}
            alternateLocales={alternateLocales}
            robots={robots}
          >
            <CuisinePageHeroContainer />
            {cuisineRestaurantsOn && <CuisineRestaurantsContainer />}

            <PageContainer>
              <PageContainer fullWidth="mobile-only">
                <RestaurantReviewsContainer />
              </PageContainer>
            </PageContainer>

            <HowDeliverooWorksContainer />

            <PageContainer>
              <LocationListContainer />

              <PageContainer fullWidth="mobile-only">
                <RecommendedCuisinesContainer />
              </PageContainer>
            </PageContainer>

            <CuisinePageFooter />
            <PageFooter />
            <AnalyticsTrackers />
          </Page>
        );
      }
      default:
        return <CustomErrorPage statusCode={statusCode} />;
    }
  }
}

Cuisines.propTypes = {
  statusCode: PropTypes.number.isRequired,
  pageTitle: PropTypes.string.isRequired,
  metaDescription: PropTypes.string.isRequired,
  landingPagePath: PropTypes.string.isRequired,
  cuisineRestaurantsOn: PropTypes.bool,
  alternateLocales: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      hrefLang: PropTypes.string.isRequired,
    }),
  ).isRequired,
  robots: PropTypes.string,
};

Cuisines.defaultProps = {
  cuisineRestaurantsOn: false,
  robots: undefined,
};

function mapStateToProps(state) {
  const { city, pageTitle, metaDescription } = state.cuisine;
  const { status } = statusForState(state);
  const alternateLocales = alternateLocaleUrlsSelector(state);
  const robots = hasQueryParamsSelector(state) ? 'noindex, nofollow' : undefined;

  return {
    statusCode: status,
    city,
    pageTitle,
    metaDescription,
    landingPagePath: getCuisineLandingPagePath(state.request),
    cuisineRestaurantsOn: isCuisineRestaurantsOn(state.config),
    alternateLocales,
    robots,
  };
}

export default connect(mapStateToProps)(withI18n(Cuisines));
